"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import type {
  AuditionPayload,
  AuditionTrack,
  InspectTrack,
  QualityFlag,
} from "@/lib/music/analyzer-types";
import type { AuditionState, MidiAuditionPlayer } from "@/lib/midi/midi-audition-player";

import { Badge } from "../../atoms/badge";
import { isDisabledTrack, isProbableTrack } from "./track-classification";
import { TrackRow } from "./track-row";

interface TrackSelectorProps {
  audition: AuditionPayload;
  inspectTracks: readonly InspectTrack[];
  player: MidiAuditionPlayer | null;
  state: AuditionState;
  selected: ReadonlySet<number>;
  onToggle: (trackIdx: number) => void;
  flags: readonly QualityFlag[];
  /** Forwarded for telemetry: fires once per solo-play click. */
  onSoloPlay?: (trackIdx: number) => void;
  /** Forwarded for telemetry: fires the first time the user expands the "others" group. */
  onOtherTracksExpanded?: (otherCount: number) => void;
}

interface ClassifiedTrack {
  trackIdx: number;
  track: AuditionTrack;
  inspectTrack: InspectTrack | undefined;
  isProbable: boolean;
  isDisabled: boolean;
}

/**
 * Classifier mirroring spec §6.3 (probable vs other) + §3.2 (disabled).
 *
 * A track is **probable** when it's a strong piano-part candidate (detected
 * melody, bass, or a piano-family track) and **disabled** only when it can't
 * be played on a piano (drum channel). See {@link isProbableTrack}.
 */
function classifyTracks(
  audition: AuditionPayload,
  inspectTracks: readonly InspectTrack[],
): ClassifiedTrack[] {
  return audition.tracks.map((track, trackIdx) => {
    const inspectTrack = inspectTracks.find((t) => t.index === track.index);

    return {
      trackIdx,
      track,
      inspectTrack,
      isProbable: isProbableTrack(track, inspectTrack),
      isDisabled: isDisabledTrack(track),
    };
  });
}

export function TrackSelector({
  audition,
  inspectTracks,
  player,
  state,
  selected,
  onToggle,
  flags,
  onSoloPlay,
  onOtherTracksExpanded,
}: TrackSelectorProps) {
  // The `flags` prop is forwarded for forward compatibility (per-flag UX
  // affordances), the selector itself doesn't branch on it today.
  void flags;

  const classified = useMemo(
    () => classifyTracks(audition, inspectTracks),
    [audition, inspectTracks],
  );

  const probable = classified.filter((c) => c.isProbable);
  const others = classified.filter((c) => !c.isProbable);

  const noProbable = probable.length === 0;
  const [othersExpanded, setOthersExpanded] = useState(noProbable);
  const expandedFiredRef = useRef(noProbable);

  // Fire the telemetry callback the first time the user expands the "others"
  // group. Ignores the initial expansion when there's no "probables".
  useEffect(() => {
    if (othersExpanded && !expandedFiredRef.current) {
      expandedFiredRef.current = true;
      onOtherTracksExpanded?.(others.length);
    }
  }, [othersExpanded, others.length, onOtherTracksExpanded]);

  const handleToggleOthers = () => {
    setOthersExpanded((prev) => !prev);
  };

  if (noProbable) {
    return (
      <div className="flex flex-col">
        <GroupHeader label="Pistes du fichier" />
        <RowList
          items={classified}
          player={player}
          state={state}
          selected={selected}
          onToggle={onToggle}
          onSoloPlay={onSoloPlay}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col">
        <GroupHeader
          label="Pistes probables"
          rightSlot={
            <Badge variant="accent">
              ★ {probable.length} trouvée{probable.length > 1 ? "s" : ""}
            </Badge>
          }
        />
        <RowList
          items={probable}
          player={player}
          state={state}
          selected={selected}
          onToggle={onToggle}
          onSoloPlay={onSoloPlay}
        />
      </div>

      {others.length > 0 && (
        <div className="flex flex-col">
          <button
            type="button"
            onClick={handleToggleOthers}
            aria-expanded={othersExpanded}
            className="flex items-center justify-between text-left transition-colors duration-150 cursor-pointer"
            style={{
              padding: "8px 16px",
              color: "var(--color-text-secondary)",
            }}
          >
            <span className="text-[12px] font-medium uppercase tracking-wide">
              {othersExpanded
                ? `Masquer les ${others.length} autres pistes`
                : `Voir les ${others.length} autres pistes`}
            </span>
            <span
              aria-hidden
              style={{
                display: "inline-block",
                transform: othersExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 200ms ease",
              }}
            >
              ▾
            </span>
          </button>
          {othersExpanded && (
            <RowList
              items={others}
              player={player}
              state={state}
              selected={selected}
              onToggle={onToggle}
              onSoloPlay={onSoloPlay}
            />
          )}
        </div>
      )}
    </div>
  );
}

interface RowListProps {
  items: readonly ClassifiedTrack[];
  player: MidiAuditionPlayer | null;
  state: AuditionState;
  selected: ReadonlySet<number>;
  onToggle: (trackIdx: number) => void;
  onSoloPlay?: (trackIdx: number) => void;
}

function RowList({
  items,
  player,
  state,
  selected,
  onToggle,
  onSoloPlay,
}: RowListProps) {
  return (
    <div>
      {items.map((item) => (
        <TrackRow
          key={item.trackIdx}
          trackIdx={item.trackIdx}
          track={item.track}
          inspectTrack={item.inspectTrack}
          player={player}
          state={state}
          isSelected={selected.has(item.trackIdx)}
          isProbable={item.isProbable}
          isDisabled={item.isDisabled}
          onToggleSelected={() => onToggle(item.trackIdx)}
          onSoloPlay={() => onSoloPlay?.(item.trackIdx)}
        />
      ))}
    </div>
  );
}

function GroupHeader({
  label,
  rightSlot,
}: {
  label: string;
  rightSlot?: React.ReactNode;
}) {
  return (
    <div
      className="flex items-center justify-between"
      style={{
        padding: "8px 16px",
        color: "var(--color-text-muted)",
      }}
    >
      <span className="text-[12px] font-medium uppercase tracking-wide">
        {label}
      </span>
      {rightSlot}
    </div>
  );
}
